import type { FC } from 'react';
import {
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubButton,
  DevHubBox,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { APPLICATION_PAGE_SIZE } from '../utils/dataLeadUtils';

export interface SelectAllDialogProps {
  open: boolean;
  onClose: () => void;
  selectAllDialogData: {
    leadLogin: string;
    targetValue: boolean;
    visibleRows: any[];
    currentPageCount: number;
  } | null;
  activeFunctionalAreaApps: any[];
  onExecuteSelectAll: (
    leadLogin: string,
    rows: any[],
    targetValue: boolean,
  ) => void;
}

/**
 * SelectAllDialog component for managing the select all dialog
 * @param param0 - Props for the component
 * @returns SelectAllDialog JSX element
 */
export const SelectAllDialog: FC<SelectAllDialogProps> = ({
  open,
  onClose,
  selectAllDialogData,
  activeFunctionalAreaApps,
  onExecuteSelectAll,
}) => {
  const handleCurrentPageOnly = () => {
    if (selectAllDialogData) {
      onExecuteSelectAll(
        selectAllDialogData.leadLogin,
        selectAllDialogData.visibleRows,
        selectAllDialogData.targetValue,
      );
      onClose();
    }
  };

  const handleAllApplications = () => {
    if (selectAllDialogData) {
      const allRows = activeFunctionalAreaApps.map(app => ({
        applicationId: app.applicationId,
        name: app.name,
      }));
      onExecuteSelectAll(
        selectAllDialogData.leadLogin,
        allRows,
        selectAllDialogData.targetValue,
      );
      onClose();
    }
  };

  return (
    <DevHubDialog open={open} onClose={onClose} maxWidth="md">
      <DevHubDialogTitle>Select All Applications</DevHubDialogTitle>

      <DevHubDialogContent>
        {selectAllDialogData && (
          <DevHubBox>
            <DevHubTypography variant="body1" style={{ marginBottom: '1rem' }}>
              You are about to{' '}
              {selectAllDialogData.targetValue ? 'assign' : 'remove'} a data
              lead
              {selectAllDialogData.targetValue ? ' to' : ' from'} applications.
              Would you like to apply this to:
            </DevHubTypography>

            {activeFunctionalAreaApps.length > APPLICATION_PAGE_SIZE && (
              <DevHubBox
                style={{
                  padding: '0.75rem',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                  border: '1px solid #bbdefb',
                  marginBottom: '1rem',
                }}
              >
                <DevHubTypography variant="body2" style={{ color: '#1976d2' }}>
                  ℹ️ <strong>Note:</strong> Additional applications have been
                  loaded since opening this dialog. The total count has updated
                  from {APPLICATION_PAGE_SIZE} to{' '}
                  {activeFunctionalAreaApps.length} applications.
                </DevHubTypography>
              </DevHubBox>
            )}

            <DevHubBox
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginTop: '1rem',
              }}
            >
              <DevHubButton variant="outlined" onClick={handleCurrentPageOnly}>
                Current Page Only ({selectAllDialogData.currentPageCount}{' '}
                applications)
              </DevHubButton>

              <DevHubButton
                variant="contained"
                color="primary"
                onClick={handleAllApplications}
              >
                All Applications ({activeFunctionalAreaApps.length}{' '}
                applications)
                {activeFunctionalAreaApps.length > APPLICATION_PAGE_SIZE && (
                  <span
                    style={{
                      fontSize: '0.85em',
                      fontWeight: 'normal',
                      marginLeft: '0.5rem',
                    }}
                  >
                    (includes{' '}
                    {activeFunctionalAreaApps.length - APPLICATION_PAGE_SIZE}{' '}
                    additional)
                  </span>
                )}
              </DevHubButton>
            </DevHubBox>
          </DevHubBox>
        )}
      </DevHubDialogContent>

      <DevHubDialogActions>
        <DevHubButton variant="outlined" onClick={onClose}>
          Cancel
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
